// Import the EPSG JSON definitions
import EPSG_28350 from 'epsg-index/s/28350.json'
import EPSG_28351 from 'epsg-index/s/28351.json'
import EPSG_28352 from 'epsg-index/s/28352.json'
import EPSG_28353 from 'epsg-index/s/28353.json'
import EPSG_28354 from 'epsg-index/s/28354.json'
import EPSG_28355 from 'epsg-index/s/28355.json'
import EPSG_28356 from 'epsg-index/s/28356.json'
import EPSG_3111 from 'epsg-index/s/3111.json'
import EPSG_3857 from 'epsg-index/s/3857.json'
import EPSG_4283 from 'epsg-index/s/4283.json'
import EPSG_4326 from 'epsg-index/s/4326.json'
import EPSG_7844 from 'epsg-index/s/7844.json'
import EPSG_7850 from 'epsg-index/s/7850.json'
import EPSG_7851 from 'epsg-index/s/7851.json'
import EPSG_7852 from 'epsg-index/s/7852.json'
import EPSG_7853 from 'epsg-index/s/7853.json'
import EPSG_7854 from 'epsg-index/s/7854.json'
import EPSG_7855 from 'epsg-index/s/7855.json'
import EPSG_7856 from 'epsg-index/s/7856.json'
import EPSG_7899 from 'epsg-index/s/7899.json'
import { containsCoordinate } from 'ol/extent'
import { addEquivalentProjections, get as olProjGet } from 'ol/proj'
import { register } from 'ol/proj/proj4'
import proj4 from 'proj4'

import type { Coordinate as OLCoordinate } from 'ol/coordinate'
import type { Projection as OlProjection } from 'ol/proj'

import { WEB_MERCATOR_PROJECTION_CODE } from './constants'

export type PoziConfigBoundingBox = [[number, number], [number, number]]
export type BoundingBox = [number, number, number, number]

export type EpsgProjection = {
  code: string
  kind: string
  name: string
  wkt: string
  proj4: string
  bbox: number[]
  unit: string
  area: string
  accuracy: number | null
}

// Map projection codes to their imported JSON modules
export const supportedProjections: Record<string, EpsgProjection> = {
  'EPSG:4326': EPSG_4326 as EpsgProjection, // WGS84 / Lat-Lon
  'EPSG:7844': EPSG_7844 as EpsgProjection, // GDA2020 / Lat-Lon
  'EPSG:7850': EPSG_7850 as EpsgProjection, // GDA2020 / MGA Zone 50
  'EPSG:7851': EPSG_7851 as EpsgProjection, // GDA2020 / MGA Zone 51
  'EPSG:7852': EPSG_7852 as EpsgProjection, // GDA2020 / MGA Zone 52
  'EPSG:7853': EPSG_7853 as EpsgProjection, // GDA2020 / MGA Zone 53
  'EPSG:7854': EPSG_7854 as EpsgProjection, // GDA2020 / MGA Zone 54
  'EPSG:7855': EPSG_7855 as EpsgProjection, // GDA2020 / MGA Zone 55
  'EPSG:7856': EPSG_7856 as EpsgProjection, // GDA2020 / MGA Zone 56
  'EPSG:7899': EPSG_7899 as EpsgProjection, // GDA2020 / Vicgrid
  'EPSG:4283': EPSG_4283 as EpsgProjection, // GDA94 / Lat-Lon
  'EPSG:28350': EPSG_28350 as EpsgProjection, // GDA94 / MGA Zone 50
  'EPSG:28351': EPSG_28351 as EpsgProjection, // GDA94 / MGA Zone 51
  'EPSG:28352': EPSG_28352 as EpsgProjection, // GDA94 / MGA Zone 52
  'EPSG:28353': EPSG_28353 as EpsgProjection, // GDA94 / MGA Zone 53
  'EPSG:28354': EPSG_28354 as EpsgProjection, // GDA94 / MGA Zone 54
  'EPSG:28355': EPSG_28355 as EpsgProjection, // GDA94 / MGA Zone 55
  'EPSG:28356': EPSG_28356 as EpsgProjection, // GDA94 / MGA Zone 56
  'EPSG:3111': EPSG_3111 as EpsgProjection, // GDA94 / Vicgrid
  'EPSG:3857': EPSG_3857 as EpsgProjection // Pseudo Mercator
}

// Official parameters for GDA94 -> GDA2020 transformation (EPSG:8048) inversed
// https://epsg.io/8048
const GDA2020_TO_GDA94_TOWGS84 = '-0.06155,0.01087,0.04019,0.0394924,0.0327221,0.0328979,0.009994'

const supportedProjectionIds = Object.keys(supportedProjections)

// --- Helper function to generate OpenGIS ID from EPSG ID
const getOpenGisId = (epsgId: string): string => {
  const projectionCode = epsgId.replace('EPSG:', '')
  return `http://www.opengis.net/gml/srs/epsg.xml#${projectionCode}`
}

// --- Register all projections in proj4 and build Projection objects
supportedProjectionIds.forEach((epsgId) => {
  const epsgJson = supportedProjections[epsgId]
  if (!epsgJson) return

  let proj4Def = epsgJson.proj4

  // Update definition to include a 7-parameter Helmert transformation for GDA94 projections
  if (epsgJson.name.includes('GDA94')) {
    // Lat-lon projections (EPSG:4283)
    proj4Def = `+proj=longlat +ellps=GRS80 +no_defs +towgs84=${GDA2020_TO_GDA94_TOWGS84}`
    if (epsgJson.kind === 'CRS-PROJCRS') {
      // Projected coordinate systems (EPSG:28350-28356)
      const zone = epsgJson.code.slice(-2)
      proj4Def = `+proj=utm +zone=${zone} +south +ellps=GRS80 +units=m +no_defs +type=crs +towgs84=${GDA2020_TO_GDA94_TOWGS84}`
    }
  }

  // Register the projection definition in proj4
  proj4.defs(epsgId, proj4Def)
  // Register the GeoServer-style equivalent projection definition in proj4
  proj4.defs(getOpenGisId(epsgId), proj4Def)
})

// Register proj4 with OpenLayers
register(proj4)

// Register the GeoServer-style projections as equivalent in OpenLayers
// See: https://github.com/openlayers/openlayers/issues/3898
supportedProjectionIds.forEach((epsgId) => {
  addEquivalentProjections([olProjGet(epsgId) as OlProjection, olProjGet(getOpenGisId(epsgId)) as OlProjection])
})

/**
 * Check if a projection is supported in Pozi
 * @param epsgId - The EPSG ID of the projection
 * @returns True if the projection is supported, false otherwise
 */
export const isSupportedProjection = (epsgId: string): boolean => {
  return epsgId in supportedProjections
}

/**
 * Check if a projection is a metre-based coordinate system
 * @param epsgId - The EPSG ID of the projection
 * @returns True if the projection is a metre-based coordinate system, false otherwise
 */
export const isMetreBasedProjection = (epsgId: string): boolean => {
  return supportedProjections[epsgId]?.unit === 'metre'
}

/**
 * Get the x-coordinate label for a projection
 * @param epsgId - The EPSG ID of the projection
 * @returns The x-coordinate label
 */
export const getXCoordinateLabel = (epsgId: string): string => {
  return isMetreBasedProjection(epsgId) ? 'Easting' : 'Longitude'
}

/**
 * Get the y-coordinate label for a projection
 * @param epsgId - The EPSG ID of the projection
 * @returns The y-coordinate label
 */
export const getYCoordinateLabel = (epsgId: string): string => {
  return isMetreBasedProjection(epsgId) ? 'Northing' : 'Latitude'
}

/**
 * Normalises the bounding box to the OpenLayers format.
 * @param bbox - The bounding box to normalise. Can be in Pozi config, OpenLayers or EPSG format.
 * @returns The normalised bounding box in OpenLayers format.
 */
export const normaliseBoundingBox = (bbox: PoziConfigBoundingBox | BoundingBox): BoundingBox => {
  // Pozi config bounding box format [[minX, maxY],[maxX, minY]]
  if (Array.isArray(bbox[0])) {
    const [[minX, maxY], [maxX, minY]] = bbox as PoziConfigBoundingBox
    return [minX, minY, maxX, maxY]
  }

  // Check for EPSG-style: [maxY, minX, minY, maxX]
  const [maxY, minX, minY, maxX] = bbox as BoundingBox
  if (maxY > minY && maxX > minX) {
    return [minX, minY, maxX, maxY]
  }

  // If the bounding box is not Pozi config or EPSG format, return it as is
  return bbox as BoundingBox
}

/**
 * Checks if two bounding boxes intersect.
 * @param bbox1 - The first bounding box to check. Must be in OpenLayers format [minX, minY, maxX, maxY].
 * @param bbox2 - The second bounding box to check. Must be in OpenLayers format [minX, minY, maxX, maxY].
 * @returns True if the bounding boxes intersect, false otherwise.
 */
export const doBoundingBoxesIntersect = (bbox1: BoundingBox, bbox2: BoundingBox): boolean => {
  const [minX1, minY1, maxX1, maxY1] = bbox1
  const [minX2, minY2, maxX2, maxY2] = bbox2
  return minX1 <= maxX2 && maxX1 >= minX2 && minY1 <= maxY2 && maxY1 >= minY2
}

/**
 * Return relevant projections to a given location (point or bounding box)
 * @param location - The location to return relevant projections for. Can be a point or an OpenLayers bounding box.
 * @param includeGda94 - Whether to include GDA94 projections. Defaults to false.
 * @returns A record of relevant projections that intersect with the given location.
 * The key is the EPSG ID and the value is the projection.
 * Pseudo Mercator (EPSG:3857) is excluded from the results.
 */
export const getRelevantProjections = (location: OLCoordinate | BoundingBox, includeGda94: boolean = false, includeVicGrid: boolean = false): Record<string, EpsgProjection> => {
  const relevantProjections: Record<string, EpsgProjection> = {}
  for (const epsgId of supportedProjectionIds.filter((id) => id !== WEB_MERCATOR_PROJECTION_CODE)) {
    const projection = supportedProjections[epsgId]
    const projectionName = projection.name.toUpperCase()
    const projectionBbox = normaliseBoundingBox(projection.bbox as BoundingBox)
    // Exclude GDA94 projections if not requested
    if (!includeGda94 && projectionName.includes('GDA94')) {
      continue
    }
    // Exclude Vicgrid projections if not requested
    if (!includeVicGrid && projectionName.includes('VICGRID')) {
      continue
    }
    // If location is a point, check if the projection's bounding box contains the point
    // A point is [number, number], not [[number, number], [number, number]] (PoziConfigBoundingBox)
    if (Array.isArray(location) && location.length === 2 && typeof location[0] === 'number') {
      const currentPoint = location as OLCoordinate
      if (containsCoordinate(projectionBbox, currentPoint)) {
        relevantProjections[epsgId] = projection
      }
      continue
    }
    // If location is a bounding box, check if the projection's bounding box intersects with the location
    const currentBbox = normaliseBoundingBox(location as BoundingBox)
    if (doBoundingBoxesIntersect(projectionBbox, currentBbox)) {
      relevantProjections[epsgId] = projection
    }
  }
  return relevantProjections
}

export { proj4 }
